import { useRef, useState, useEffect, type ReactNode } from 'react';

interface ScrollSyncTableProps {
  children: ReactNode;
  className?: string;
}

/**
 * Wraps a wide table in a horizontally-scrollable container that also exposes a SECOND
 * scrollbar mirrored at the TOP (above the table header). This makes horizontal scroll
 * discoverable even when the table is long and the native bottom scrollbar sits below the
 * fold.
 *
 * - The top proxy bar is two-way-synced with the real scroll container (guarded against
 *   feedback loops with a `syncing` ref + requestAnimationFrame).
 * - It only mounts when the content actually overflows, so there is zero chrome when the
 *   table fits the viewport (e.g. after responsive columns are hidden).
 * - Touch devices keep native panning; the proxy is hidden under `pointer: coarse`.
 *
 * Sticky/frozen columns and responsive column hiding are applied by the caller on the
 * <table> cells — this wrapper only owns the scroll affordance.
 */
export function ScrollSyncTable({ children, className }: ScrollSyncTableProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => {
      setScrollWidth(el.scrollWidth);
      setOverflowing(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // Observe the <table> too, so re-measuring happens when columns mount/unmount
    // (internal vs external user, responsive xl: breakpoint, new columns) or data loads.
    const table = el.firstElementChild;
    if (table) ro.observe(table);
    return () => ro.disconnect();
  }, []);

  const sync = (from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (!from || !to || syncing.current) return;
    syncing.current = true;
    to.scrollLeft = from.scrollLeft;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  };

  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${className ?? ''}`}
    >
      {overflowing && (
        <div
          ref={topRef}
          onScroll={() => sync(topRef.current, bodyRef.current)}
          aria-hidden="true"
          className="overflow-x-auto brand-scrollbar h-3 border-b border-slate-100 [@media(pointer:coarse)]:hidden"
        >
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
      )}
      <div
        ref={bodyRef}
        onScroll={() => sync(bodyRef.current, topRef.current)}
        className="overflow-x-auto brand-scrollbar"
      >
        {children}
      </div>
    </div>
  );
}
